import {useEffect, useRef} from "react";
import {artClass} from "../public/js/utils/sanitize.js";

// The lobby's deckbuilder for a signed-in player. Every number, limit, tab and line of
// copy comes from controller.deckBuilder() (deckBuilder.js), where the coverage gate sees
// it; this only paints it and wires the buttons. On a phone it is the whole screen: the
// top bar and the save bar pin, the summary scrolls away, and the tabs stick.
export function DeckBuilder({controller, view}) {
  const scroller = useRef(null);
  const summary = useRef(null);

  // A new tab starts at its own first card. Once the summary has scrolled off it stays
  // off, rather than the new list opening wherever the old one was left. The summary's
  // height is where the tabs start: the tabs' own offsetTop moves while they stick.
  useEffect(() => {
    const box = scroller.current;
    box.scrollTop = Math.min(box.scrollTop, summary.current.offsetHeight);
  }, [view.activeTab, view.onlyInDeck]);

  return (
    <section className={`deck-builder deck-tone-${view.tone}`} aria-label="Bygg din kortlek">
      <header className="deck-topbar">
        <button
          className={view.closeArmed ? "deck-bar-button deck-close armed" : "deck-bar-button deck-close"}
          type="button"
          onClick={() => controller.closeDeckBuilder()}
        >
          <ChevronIcon />
          {view.closeLabel}
        </button>
        <button className="deck-bar-button deck-reset" type="button" onClick={() => controller.resetDeckDraft()}>
          Återställ
        </button>
      </header>

      <div className="deck-scroll" ref={scroller}>
        <div className="deck-summary" ref={summary}>
          <div className="deck-headline">
            <div>
              <h2>Din kortlek</h2>
              <p className="deck-total">{view.countLabel}</p>
            </div>
            <figure className="deck-curve" role="img" aria-label={view.curveLabel}>
              {view.curve.map((bar) => (
                <span key={bar.cost} className="deck-curve-bar">
                  <span className="deck-curve-count">{bar.count || ""}</span>
                  <span className="deck-curve-track">
                    <span className="deck-curve-fill" style={{"--share": bar.share}}></span>
                  </span>
                  <span className="deck-curve-cost">{bar.cost}</span>
                </span>
              ))}
            </figure>
          </div>

          <div className="deck-opening">
            <p className="deck-opening-label">{view.openingLabel}</p>
            <ol className="deck-slots">
              {view.slots.map((slot, position) => (
                <li key={slot.card ? `${position}-${slot.card.id}` : position}>
                  {slot.card ? (
                    <button
                      className="deck-slot filled"
                      type="button"
                      aria-label={slot.label}
                      onClick={() => controller.unpickOpeningCard(slot.index)}
                    >
                      <span className={`deck-slot-art ${artClass(slot.card.image)}`}></span>
                      <span className="deck-slot-name">{slot.card.name}</span>
                      <span className="deck-slot-clear"><CrossIcon /></span>
                    </button>
                  ) : (
                    <span className="deck-slot empty">
                      <StarIcon filled={false} />
                      <span className="deck-visually-hidden">{slot.label}</span>
                    </span>
                  )}
                </li>
              ))}
            </ol>
            <p className="deck-help">
              Max två av varje kort. Tryck på stjärnan för att välja upp till fyra startkort —
              de delas ut när värden väljer egen starthand.
            </p>
          </div>
        </div>

        <div className="deck-tabs" role="tablist" aria-label="Kortsorter">
          {view.tabs.map((tab) => (
            <button
              key={tab.id}
              id={`deck-tab-${tab.id}`}
              className={`deck-tab deck-tone-${tab.tone}`}
              type="button"
              role="tab"
              aria-selected={tab.active}
              aria-controls="deck-panel"
              onClick={() => controller.chooseDeckTab(tab.id)}
            >
              <span className="deck-tab-label">{tab.label}</span>
              <span className="deck-tab-count" aria-label={`${tab.inDeck} i leken`}>{tab.inDeck}</span>
            </button>
          ))}
        </div>

        <div className="deck-panel" id="deck-panel" role="tabpanel" aria-labelledby={`deck-tab-${view.activeTab}`}>
          <div className="deck-section-head">
            <div>
              <h3>{view.sectionTitle}</h3>
              <p>{view.sectionLine}</p>
            </div>
            <button
              className="deck-filter"
              type="button"
              role="switch"
              aria-checked={view.onlyInDeck}
              onClick={() => controller.toggleDeckFilter()}
            >
              <span className="deck-filter-track"><span className="deck-filter-thumb"></span></span>
              {view.filterLabel}
            </button>
          </div>

          {view.emptyText ? (
            <div className="deck-empty">
              <p>{view.emptyText}</p>
              <button className="plain-button" type="button" onClick={() => controller.toggleDeckFilter()}>
                Visa alla kort
              </button>
            </div>
          ) : (
            <ul className="deck-list">
              {view.rows.map((row) => (
                <li key={row.card.id} className={row.count ? "deck-row in-deck" : "deck-row"}>
                  <span className="deck-card">
                    <span className={`deck-art ${artClass(row.card.image)}`}></span>
                    <span className="deck-cost" aria-label={`${row.card.cost} mana`}>{row.card.cost}</span>
                  </span>
                  <div className="deck-info">
                    <p className="deck-title">
                      <span className="deck-name">{row.card.name}</span>
                      {row.isNew && <em className="deck-new">Ny</em>}
                    </p>
                    <p className="deck-text">{row.card.text}</p>
                  </div>
                  <button
                    className="deck-pick"
                    type="button"
                    aria-label={`Startkort: ${row.card.name}`}
                    aria-pressed={row.picked > 0}
                    disabled={!row.canPick}
                    onClick={() => controller.toggleOpeningCard(row.card.id)}
                  >
                    {row.stars.map((filled, index) => <StarIcon key={index} filled={filled} />)}
                  </button>
                  <div className="deck-stepper">
                    <button
                      className="deck-step"
                      type="button"
                      aria-label={`Ta bort ${row.card.name}`}
                      disabled={!row.canRemove}
                      onClick={() => controller.removeDeckCard(row.card.id)}
                    >
                      <MinusIcon />
                    </button>
                    <span className="deck-pips" role="img" aria-label={`${row.count} i leken`}>
                      {row.pips.map((on, index) => <span key={index} className={on ? "deck-pip on" : "deck-pip"}></span>)}
                    </span>
                    <button
                      className="deck-step"
                      type="button"
                      aria-label={`Lägg till ${row.card.name}`}
                      disabled={!row.canAdd}
                      onClick={() => controller.addDeckCard(row.card.id)}
                    >
                      <PlusIcon />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <footer className="deck-savebar">
        <p className="deck-hint" aria-live="polite">{view.hint}</p>
        <button className="primary-button deck-save" type="button" disabled={!view.canSave} onClick={() => controller.saveDeck()}>
          {view.saveLabel}
        </button>
      </footer>
    </section>
  );
}

// One stroke family for the builder's controls: 24-unit box, 2px round strokes.
function Icon({children, className = "deck-icon"}) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {children}
    </svg>
  );
}

function StarIcon({filled}) {
  return (
    <Icon className={filled ? "deck-icon deck-star filled" : "deck-icon deck-star"}>
      <path d="M12 3.6l2.6 5.3 5.8.8-4.2 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8-4.2-4.1 5.8-.8z" />
    </Icon>
  );
}

function MinusIcon() {
  return <Icon><path d="M6 12h12" /></Icon>;
}

function PlusIcon() {
  return <Icon><path d="M12 6v12M6 12h12" /></Icon>;
}

function CrossIcon() {
  return <Icon><path d="M7.5 7.5l9 9M16.5 7.5l-9 9" /></Icon>;
}

function ChevronIcon() {
  return <Icon><path d="M14.5 6l-6 6 6 6" /></Icon>;
}
